/**
 * Advanced Memory Management Service
 * Handles progressive cleanup, streaming results, and smart garbage collection
 */

interface MemoryConfig {
  maxMemoryMB: number;
  cleanupThreshold: number;
  chunkSize: number;
  gcInterval: number;
}

interface StreamingBuffer<T> {
  data: T[];
  maxSize: number;
  onFlush?: (data: T[]) => void;
}

export class MemoryManager {
  private static instance: MemoryManager;
  
  private config: MemoryConfig;
  private buffers = new Map<string, StreamingBuffer<any>>();
  private gcTimer: NodeJS.Timeout | null = null;
  private memoryUsage = { heap: 0, external: 0 };
  
  private constructor(config: Partial<MemoryConfig> = {}) {
    this.config = {
      maxMemoryMB: 256, // 256MB limit
      cleanupThreshold: 0.8, // Cleanup at 80% usage
      chunkSize: 1000, // Process 1000 items at a time
      gcInterval: 30000, // GC check every 30 seconds
      ...config
    };
    
    this.startGarbageCollector();
  }

  static getInstance(config?: Partial<MemoryConfig>): MemoryManager {
    if (!MemoryManager.instance) {
      MemoryManager.instance = new MemoryManager(config);
    }
    return MemoryManager.instance;
  }

  /**
   * Create streaming buffer for progressive processing
   */
  createStreamingBuffer<T>(
    id: string, 
    options: {
      maxSize?: number;
      onFlush?: (data: T[]) => void;
    } = {}
  ): void {
    const { maxSize = this.config.chunkSize, onFlush } = options;
    
    this.buffers.set(id, {
      data: [],
      maxSize,
      onFlush
    });
  }

  /**
   * Add data to streaming buffer
   */
  addToBuffer<T>(id: string, item: T | T[]): void {
    const buffer = this.buffers.get(id);
    if (!buffer) {
      throw new Error(`Buffer ${id} not found`);
    }

    const items = Array.isArray(item) ? item : [item];
    buffer.data.push(...items);

    // Auto-flush when buffer is full
    if (buffer.data.length >= buffer.maxSize) {
      this.flushBuffer(id);
    }
  }

  /**
   * Flush buffer and trigger callback
   */
  flushBuffer(id: string): void {
    const buffer = this.buffers.get(id);
    if (!buffer || buffer.data.length === 0) return;

    const dataToFlush = [...buffer.data];
    buffer.data = []; // Clear buffer

    if (buffer.onFlush) {
      // Process in RequestAnimationFrame to prevent UI blocking
      requestAnimationFrame(() => {
        buffer.onFlush!(dataToFlush);
      });
    }
  }

  /**
   * Process large array in chunks without blocking UI
   */
  async processInChunks<T, R>(
    data: T[],
    processor: (chunk: T[]) => Promise<R[]> | R[],
    options: {
      chunkSize?: number;
      onProgress?: (processed: number, total: number, results: R[]) => void;
      useRAF?: boolean;
    } = {}
  ): Promise<R[]> {
    const { 
      chunkSize = this.config.chunkSize, 
      onProgress,
      useRAF = true 
    } = options;
    
    const results: R[] = [];
    const totalChunks = Math.ceil(data.length / chunkSize);
    
    for (let i = 0; i < data.length; i += chunkSize) {
      const chunk = data.slice(i, i + chunkSize);
      
      try {
        const chunkResults = await processor(chunk);
        results.push(...chunkResults);
        
        onProgress?.(i + chunk.length, data.length, results);
        
        // Yield control to prevent UI blocking
        if (useRAF && i + chunkSize < data.length) {
          await this.yieldToUI();
        }
        
        // Memory pressure check
        if (this.isMemoryPressureHigh()) {
          await this.performEmergencyCleanup();
        }
        
      } catch (error) {
        console.error(`Chunk processing failed at position ${i}:`, error);
        // Continue with next chunk
      }
    }
    
    return results;
  }

  /**
   * Yield control to UI using RequestAnimationFrame
   */
  private yieldToUI(): Promise<void> {
    return new Promise(resolve => {
      requestAnimationFrame(() => resolve());
    });
  }

  /**
   * Check if memory pressure is high
   */
  private isMemoryPressureHigh(): boolean {
    try {
      // Check if performance.memory is available (Chrome/Edge)
      if ('memory' in performance) {
        const memory = (performance as any).memory;
        const usedMB = memory.usedJSHeapSize / (1024 * 1024);
        const limitMB = memory.jsHeapSizeLimit / (1024 * 1024);
        
        return usedMB > (limitMB * this.config.cleanupThreshold);
      }
      
      // Fallback: estimate based on buffer sizes
      const totalBufferSize = Array.from(this.buffers.values())
        .reduce((sum, buffer) => sum + buffer.data.length, 0);
        
      return totalBufferSize > (this.config.maxMemoryMB * 1024); // Rough estimate
    } catch {
      return false;
    }
  }

  /**
   * Perform emergency cleanup
   */
  private async performEmergencyCleanup(): Promise<void> {
    console.warn('Memory pressure detected, performing emergency cleanup...');
    
    // Flush all buffers
    for (const bufferId of this.buffers.keys()) {
      this.flushBuffer(bufferId);
    }
    
    // Clear completed buffers
    const toDelete: string[] = [];
    this.buffers.forEach((buffer, id) => {
      if (buffer.data.length === 0) {
        toDelete.push(id);
      }
    });
    
    toDelete.forEach(id => this.buffers.delete(id));
    
    // Suggest garbage collection (if available)
    if ('gc' in window && typeof (window as any).gc === 'function') {
      (window as any).gc();
    }
    
    // Small delay to let cleanup take effect
    await new Promise(resolve => setTimeout(resolve, 100));
  }

  /**
   * Start automatic garbage collector
   */
  private startGarbageCollector(): void {
    this.gcTimer = setInterval(() => {
      this.performRoutineCleanup();
    }, this.config.gcInterval);
  }

  /**
   * Perform routine cleanup
   */
  private performRoutineCleanup(): void {
    // Clean empty buffers
    const emptyBuffers: string[] = [];
    this.buffers.forEach((buffer, id) => {
      if (buffer.data.length === 0) {
        emptyBuffers.push(id);
      }
    });
    
    emptyBuffers.forEach(id => this.buffers.delete(id));
    
    // Update memory usage stats
    this.updateMemoryStats();
  }

  /**
   * Update memory usage statistics
   */
  private updateMemoryStats(): void {
    try {
      if ('memory' in performance) {
        const memory = (performance as any).memory;
        this.memoryUsage = {
          heap: Math.round(memory.usedJSHeapSize / (1024 * 1024)),
          external: Math.round((memory.totalJSHeapSize - memory.usedJSHeapSize) / (1024 * 1024))
        };
      }
    } catch {
      // Ignore errors
    }
  }

  /**
   * Get memory statistics
   */
  getMemoryStats() {
    this.updateMemoryStats();
    
    return {
      heapUsedMB: this.memoryUsage.heap,
      externalMB: this.memoryUsage.external,
      buffersCount: this.buffers.size,
      totalBufferItems: Array.from(this.buffers.values())
        .reduce((sum, buffer) => sum + buffer.data.length, 0),
      isHighPressure: this.isMemoryPressureHigh()
    };
  }

  /**
   * Destroy buffer and clean up
   */
  destroyBuffer(id: string): void {
    const buffer = this.buffers.get(id);
    if (buffer) {
      // Final flush if there's data
      if (buffer.data.length > 0) {
        this.flushBuffer(id);
      }
      this.buffers.delete(id);
    }
  }

  /**
   * Clean up all resources
   */
  destroy(): void {
    if (this.gcTimer) {
      clearInterval(this.gcTimer);
      this.gcTimer = null;
    }
    
    // Flush all buffers before destroying
    for (const bufferId of this.buffers.keys()) {
      this.flushBuffer(bufferId);
    }
    
    this.buffers.clear();
  }
}